import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import type { Tarif } from '../billtoro/types';
import { Dialog } from '../components/Dialog';
import { canShareFiles, ShareFile } from '../components/ShareFile';
import { fmtDate, fmtTime } from '../lib/format';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { darf } from './tarifHinweis';
import type { EFormat, KundenArt, Versand } from './types';
import './versand.css';

// Per E-Mail an den Kunden (server/src/modules/rechnungstool/versand.ts): Empfänger aus der Kundenliste, Betreff und
// Text mit den Werten der Rechnung schon eingesetzt, Anhänge zum Nachsehen. Selten gebraucht und darum aufklappbar:
// den Text als Standard für alle Rechnungen (bzw. Mahnungen, Angebote, Auftragsbestätigungen, Lieferscheine) speichern.
// Am Handy daneben „Teilen“ (z. B. Messenger), am Rechner „PDF herunterladen“.

export type BelegArt = 'rechnung' | 'mahnung' | 'angebot' | 'auftrag' | 'lieferschein';
export interface VersandBeleg {
  art: BelegArt;
  id: string;
  titel: string;
}

interface Info {
  clientId: string;
  titel: string;
  vorlage: { art: string; name: string; betreff: string; text: string; standard: boolean };
  werte: Record<string, string>;
  platzhalter: { name: string; text: string }[];
  an: string[];
  cc: string[];
  quelle: 'kunde' | 'rechnung' | null;
  betreff: string;
  text: string;
  signatur: string[];
  absender: string;
  replyTo: string | null;
  anhaenge: { name: string; bytes: number; url: string }[];
  eFormat: EFormat | null;
  kundeArt: KundenArt | null;
  hinweis: 'behoerde' | null;
  // Mahnung zu einer bezahlten oder stornierten Rechnung: Grund, warum sie nicht mehr rausgeht
  erledigt: string | null;
  mailAn: boolean;
  maxEmpfaenger: number;
  verlauf: { am: string; an: string; cc: string | null; status: 'gesendet' | 'fehler'; fehler: string | null }[];
}

// Wie auf dem Server (versand-mail.ts): Platzhalter einsetzen, bei leerem Wert ohne das Leerzeichen davor
const KEY = /( ?)\{([a-zA-ZäöüÄÖÜß_]+)\}/g;
function fuellen(s: string, w: Record<string, string>) {
  return s.replace(KEY, (all, space: string, key: string) => (key in w ? (w[key] ? `${space}${w[key]}` : '') : all));
}

// Umgekehrt: die Werte dieser Rechnung im Text wieder durch Platzhalter ersetzen (für „Als Standard speichern“).
// Längere Werte zuerst (Firma vor Kunde, wenn der eine im anderen steckt); sehr kurze Werte bleiben stehen.
function zuVorlage(s: string, w: Record<string, string>) {
  const pairs = Object.entries(w)
    .filter(([, v]) => v.trim().length >= 3)
    .sort((a, b) => b[1].length - a[1].length);
  let out = s;
  pairs.forEach(([, v], i) => {
    out = out.split(v).join(`\u0000${i}\u0000`);
  });
  return out.replace(/\u0000(\d+)\u0000/g, (_, i: string) => `{${pairs[Number(i)]![0]}}`);
}

const BILLTORO = isBilltoro();
// Adressen in „An“ bzw. „Kopie an“ (wie der Server: durch Komma, Semikolon oder Leerzeichen getrennt)
const zahlAdressen = (s: string) => s.split(/[\s,;]+/).filter(Boolean).length;

const kb = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1).replace('.', ',')} MB`);
const ext = (name: string) => name.split('.').pop()?.toUpperCase() ?? '';
const when = (iso: string) => `${fmtDate(iso)}, ${fmtTime(iso)} Uhr`;
const MERK = 'rt-versand-kopie';
const merk = () => {
  try {
    return localStorage.getItem(MERK) === '1';
  } catch {
    return false;
  }
};

export function VersandDialog({
  beleg,
  onClose,
  onSent,
  tarif,
}: {
  beleg: VersandBeleg | null;
  onClose(): void;
  onSent(): Promise<void> | void;
  // Billtoro: höchstens info.maxEmpfaenger Empfänger; den Standardtext ändert nur, wer das Recht „Einstellungen“ hat
  tarif?: Tarif | null;
}) {
  const toast = useToast();
  const [info, setInfo] = useState<Info | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [an, setAn] = useState('');
  const [cc, setCc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [kopie, setKopie] = useState(merk);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; code?: string } | null>(null);
  // Standardtext: aufgeklappt mit Betreff und Text als Vorlage (Platzhalter statt Werte)
  const [std, setStd] = useState(false);
  const [vBetreff, setVBetreff] = useState('');
  const [vText, setVText] = useState('');
  const [vBusy, setVBusy] = useState(false);
  const [vErr, setVErr] = useState<string | null>(null);
  const vTextRef = useRef<HTMLTextAreaElement>(null);
  const key = beleg ? `${beleg.art}:${beleg.id}` : null;

  useEffect(() => {
    if (!beleg) return;
    let alive = true;
    setInfo(null);
    setLoadErr(null);
    setError(null);
    setStd(false);
    setVErr(null);
    api
      .get<Info>(`/api/rechnungstool/${beleg.art}/${beleg.id}/versand`)
      .then((i) => {
        if (!alive) return;
        setInfo(i);
        setAn(i.an.join(', '));
        setCc(i.cc.join(', '));
        setShowCc(i.cc.length > 0);
        setBetreff(i.betreff);
        setText(i.text);
      })
      .catch((err) => alive && setLoadErr(errorText(err)));
    return () => {
      alive = false;
    };
    // nur beim Öffnen für einen anderen Beleg neu laden
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  function pickKopie(v: boolean) {
    setKopie(v);
    try {
      localStorage.setItem(MERK, v ? '1' : '0');
    } catch {
      // ohne Speicher im Browser: gilt nur jetzt
    }
  }

  async function send() {
    if (!beleg || !info) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/rechnungstool/${beleg.art}/${beleg.id}/versand`, {
        an,
        cc: showCc ? cc : '',
        betreff,
        text,
        kopieAnMich: kopie && !!info.replyTo,
      });
    } catch (err) {
      setError({ text: errorText(err), code: err instanceof ApiError ? err.code : undefined });
      setBusy(false);
      return;
    }
    // verschickt: Fenster zu, dann die Liste neu laden (ein Fehler dabei heißt nicht, dass die E-Mail nicht raus ist)
    toast(`E-Mail an ${an.split(/[\s,;]+/).filter(Boolean).join(', ')} verschickt`);
    setBusy(false);
    onClose();
    try {
      await onSent();
    } catch {
      // die Liste zeigt den Versand beim nächsten Laden
    }
  }

  function openStd(open: boolean) {
    setStd(open);
    setVErr(null);
    if (open && info) {
      setVBetreff(zuVorlage(betreff, info.werte));
      setVText(zuVorlage(text, info.werte));
    }
  }

  function insert(p: string) {
    const el = vTextRef.current;
    if (!el) return setVText((t) => t + p);
    const [a, b] = [el.selectionStart, el.selectionEnd];
    const next = vText.slice(0, a) + p + vText.slice(b);
    setVText(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(a + p.length, a + p.length);
    });
  }

  async function saveStd(standard: boolean) {
    if (!info) return;
    setVBusy(true);
    setVErr(null);
    try {
      const v = await api.put<Info['vorlage']>(
        `/api/rechnungstool/${info.clientId}/einstellungen/mail`,
        standard ? { art: info.vorlage.art, standard: true } : { art: info.vorlage.art, betreff: vBetreff, text: vText },
      );
      setInfo({ ...info, vorlage: { ...info.vorlage, ...v } });
      setBetreff(fuellen(v.betreff, info.werte).replace(/\s+/g, ' ').trim());
      setText(fuellen(v.text, info.werte));
      setStd(false);
      const alle = mehrzahl(info.vorlage);
      toast(standard ? `Wieder der Standardtext für alle ${alle}` : `Gespeichert: Text für alle ${alle}`);
    } catch (err) {
      setVErr(errorText(err));
    } finally {
      setVBusy(false);
    }
  }

  const pdf = info?.anhaenge.find((x) => x.name.toLowerCase().endsWith('.pdf'));
  const zuletzt = info?.verlauf.find((x) => x.status === 'gesendet');
  // wie oft schon verschickt (der Server schickt die letzten 20 Versuche)
  const gesendet = info?.verlauf.filter((x) => x.status === 'gesendet').length ?? 0;
  const fehler = info?.verlauf[0]?.status === 'fehler' ? info.verlauf[0] : null;
  // Billtoro: mehr Empfänger als der Tarif erlaubt (An und Kopie zusammen; der Server lehnt sonst mit „zu_viele“ ab)
  const zuViele = BILLTORO && !!info && zahlAdressen(an) + (showCc ? zahlAdressen(cc) : 0) > info.maxEmpfaenger;
  const ready = !!info && info.mailAn && !!info.replyTo && !info.erledigt && !zuViele;
  const share = canShareFiles();

  return (
    <Dialog
      open={!!beleg}
      onClose={onClose}
      // „per E-Mail“ bleibt am Handy zusammen (sonst „per E-“ / „Mail“)
      title={
        beleg ? (
          <>
            {beleg.titel} <span className="vs-nw">per E-Mail</span>
          </>
        ) : (
          ''
        )
      }
      className="vs-dlg"
    >
      {!info ? (
        loadErr ? (
          <>
            <div className="notice bad">{loadErr}</div>
            <div className="dlg-actions">
              <button type="button" className="btn" onClick={onClose}>
                Schließen
              </button>
            </div>
          </>
        ) : (
          <p className="muted">Wird geladen …</p>
        )
      ) : (
        <>
          {!info.mailAn && (
            <div className="notice warn">
              Auf diesem Server ist der E-Mail-Versand nicht eingerichtet: Die E-Mail kann nicht verschickt werden. Bitte laden Sie die
              PDF herunter{share ? ' oder teilen Sie sie' : ''}.
            </div>
          )}
          {info.erledigt && <div className="notice warn">{info.erledigt}</div>}
          {info.mailAn && !info.replyTo && (
            <div className="notice warn vs-note">
              <span>Bitte tragen Sie zuerst Ihre E-Mail-Adresse ein. Die Antworten Ihrer Kunden gehen dorthin.</span>
              <Link to="/rechnung/angaben" className="btn small">
                Meine Angaben
              </Link>
            </div>
          )}
          {fehler ? (
            <p className="vs-last bad">
              Am {when(fehler.am)} ging die E-Mail an {fehler.an} nicht raus. {fehler.fehler}
            </p>
          ) : (
            zuletzt && (
              <p className="vs-last">
                Schon verschickt am {when(zuletzt.am)} an {zuletzt.an}
                {zuletzt.cc ? ` (Kopie: ${zuletzt.cc})` : ''}.{gesendet > 1 ? ` Insgesamt ${gesendet}× verschickt.` : ''}
              </p>
            )
          )}

          <label className="field vs-an">
            <span>An</span>
            <input
              value={an}
              type="text"
              inputMode="email"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="kunde@firma.de"
              aria-describedby="vs-an-hint"
              onChange={(e) => setAn(e.target.value)}
            />
          </label>
          <small id="vs-an-hint" className="vs-an-hint">
            {info.quelle === 'kunde'
              ? 'Aus der Kundenliste. '
              : info.quelle === 'rechnung'
                ? 'Von der Rechnung. '
                : 'Für diesen Kunden ist keine E-Mail-Adresse gespeichert. '}
            {BILLTORO ? `Mehrere Adressen mit Komma trennen, höchstens ${info.maxEmpfaenger} mit Kopie.` : 'Mehrere Adressen mit Komma trennen.'}
          </small>
          {showCc ? (
            <label className="field">
              <span>Kopie an</span>
              <input
                value={cc}
                type="text"
                inputMode="email"
                autoComplete="off"
                autoCapitalize="off"
                spellCheck={false}
                placeholder="z. B. bauleiter@firma.de"
                onChange={(e) => setCc(e.target.value)}
              />
            </label>
          ) : (
            <button type="button" className="linkbtn vs-add" onClick={() => setShowCc(true)}>
              + Kopie an weitere Adresse
            </button>
          )}
          {zuViele && (
            <div className="notice warn">
              Bitte höchstens {info.maxEmpfaenger} Empfänger eintragen (An und Kopie zusammen).
              {tarif?.plan === 'kostenlos' ? ' Mehr gehen mit einem bezahlten Tarif.' : ''}
            </div>
          )}
          <label className="field">
            <span>Betreff</span>
            <input value={betreff} maxLength={200} onChange={(e) => setBetreff(e.target.value)} />
          </label>
          <label className="field vs-text">
            <span>Text</span>
            <textarea value={text} rows={9} maxLength={5000} onChange={(e) => setText(e.target.value)} />
          </label>
          {info.signatur.length > 0 && (
            <div className="vs-sig" aria-label="Darunter steht">
              <b>{info.signatur[0]}</b>
              {info.signatur.slice(1).map((l) => (
                <span key={l}>{l}</span>
              ))}
            </div>
          )}

          <ul className="vs-files" aria-label="Anhänge">
            {info.anhaenge.map((x) => (
              <li key={x.name}>
                <a href={x.url} target="_blank" rel="noreferrer">
                  <span className="vs-ext">{ext(x.name)}</span>
                  <span className="vs-fname">{x.name}</span>
                  <small>{kb(x.bytes)}</small>
                </a>
              </li>
            ))}
          </ul>
          <Hinweis info={info} />

          {info.mailAn && info.replyTo && (
            <label className="check vs-kopie">
              <input type="checkbox" checked={kopie} onChange={(e) => pickKopie(e.target.checked)} />
              <span>Kopie an mich ({info.replyTo})</span>
            </label>
          )}
          {info.mailAn && (
            <p className="vs-from">
              In der E-Mail ist ein Knopf zur Ansicht im Browser, {info.anhaenge.length > 1 ? 'die Dateien hängen' : 'die PDF hängt'}{' '}
              zusätzlich an.
            </p>
          )}
          {info.mailAn && (
            <p className="vs-from">
              Als Absender steht „{info.absender}“.{info.replyTo ? ` Antworten gehen an ${info.replyTo}.` : ''}
            </p>
          )}

          {darf(tarif, 'einstellungen') && (
            <details className="vs-std" open={std} onToggle={(e) => openStd((e.target as HTMLDetailsElement).open)}>
              <summary>Text für alle {mehrzahl(info.vorlage)} speichern</summary>
              {std && (
                <div className="vs-std-b">
                  <p className="rt-hint">
                    So wird der Text gespeichert. Die Platzhalter in geschweiften Klammern füllt die Seite jedes Mal mit den passenden Werten.
                    Tippen Sie auf einen Platzhalter, um ihn einzufügen.
                  </p>
                  <div className="vs-ph" role="group" aria-label="Platzhalter">
                    {info.platzhalter.map((p) => (
                      <button key={p.name} type="button" title={p.text} onClick={() => insert(p.name)}>
                        {p.name}
                      </button>
                    ))}
                  </div>
                  <label className="field">
                    <span>Betreff (Vorlage)</span>
                    <input value={vBetreff} maxLength={200} onChange={(e) => setVBetreff(e.target.value)} />
                  </label>
                  <label className="field">
                    <span>Text (Vorlage)</span>
                    <textarea ref={vTextRef} value={vText} rows={7} maxLength={5000} onChange={(e) => setVText(e.target.value)} />
                  </label>
                  {vErr && <div className="notice bad">{vErr}</div>}
                  <div className="vs-std-act">
                    {!info.vorlage.standard && (
                      <button type="button" className="linkbtn" disabled={vBusy} onClick={() => void saveStd(true)}>
                        Standardtext wiederherstellen
                      </button>
                    )}
                    <button type="button" className="btn small" disabled={vBusy} onClick={() => void saveStd(false)}>
                      Als Standard speichern
                    </button>
                  </div>
                </div>
              )}
            </details>
          )}

          {error && (
            <div className="notice bad vs-note">
              <span>{error.text}</span>
              {error.code === 'no_reply_to' && (
                <Link to="/rechnung/angaben" className="btn small">
                  Meine Angaben
                </Link>
              )}
            </div>
          )}
          <div className="dlg-actions vs-actions">
            {pdf &&
              (share ? (
                <ShareFile url={pdf.url} name={pdf.name} className={`btn vs-share${info.mailAn ? '' : ' primary'}`} />
              ) : (
                <a className={`btn vs-share${info.mailAn ? '' : ' primary'}`} href={`${pdf.url}?download=1`}>
                  PDF herunterladen
                </a>
              ))}
            <span className="vs-right">
              <button type="button" className="btn" onClick={onClose}>
                Abbrechen
              </button>
              {/* ohne E-Mail-Versand auf dem Server kein Senden, dafür die PDF als Hauptknopf */}
              {info.mailAn && (
                <button type="button" className="btn primary" disabled={busy || !ready} onClick={() => void send()}>
                  {busy ? 'Wird verschickt …' : 'Senden'}
                </button>
              )}
            </span>
          </div>
        </>
      )}
    </Dialog>
  );
}

// „Text für alle … speichern“ je Art des Textes (versand-mail.ts, VORLAGEN)
const MEHRZAHL: Record<string, string> = {
  rechnung: 'Rechnungen',
  storno: 'Stornorechnungen',
  korrektur: 'Rechnungskorrekturen',
  mahnung1: 'Zahlungserinnerungen',
  mahnung2: 'Mahnungen',
  mahnung3: 'letzten Mahnungen',
  angebot: 'Angebote',
  auftrag: 'Auftragsbestätigungen',
  lieferschein: 'Lieferscheine',
};
const mehrzahl = (v: Info['vorlage']) => MEHRZAHL[v.art] ?? v.name;

// Kleiner Hinweis zur E-Rechnung (keine Paragrafen): Firmen und Privatkunden bekommen die PDF mit der E-Rechnung darin,
// bei XRechnung geht die XML mit; Behörden ohne bekannte E-Mail für Rechnungen meist über ihr Portal
function Hinweis({ info }: { info: Info }) {
  if (info.hinweis === 'behoerde') {
    const xml = info.anhaenge.find((x) => x.name.toLowerCase().endsWith('.xml'));
    return (
      <p className="rt-hint vs-hint warn">
        Behörden nehmen Rechnungen meist nur über ihr Rechnungsportal an:{' '}
        {xml ? (
          <a href={xml.url} download>
            XRechnung herunterladen
          </a>
        ) : (
          'Rechnung herunterladen'
        )}{' '}
        und dort hochladen. Kennen Sie eine E-Mail-Adresse der Behörde für Rechnungen, tragen Sie sie oben ein.
      </p>
    );
  }
  if (info.eFormat === 'xrechnung') {
    return <p className="rt-hint vs-hint">Die XRechnung (XML) geht als Hauptanhang mit, die PDF zum Ansehen.</p>;
  }
  if (info.eFormat === 'zugferd') {
    return <p className="rt-hint vs-hint">Ihr Kunde bekommt die Rechnung als PDF, die E-Rechnung steckt darin.</p>;
  }
  return null;
}

// Im Fenster der PDF (Angebote, Aufträge): wann und an wen zuletzt per E-Mail verschickt und wie oft (die Liste nennt
// keine Adressen). versand: neueste zuerst. null: noch nie verschickt. „(2× verschickt)“ bricht nicht in sich um.
export function versandText(versand: Versand[]): string | null {
  const v = versand[0];
  if (!v) return null;
  return `Per E-Mail am ${fmtDate(v.am)} an ${v.an}${versand.length > 1 ? ` (${versand.length}×\u00a0verschickt)` : ''}`;
}
